import { inject, InjectionToken, Provider, ProviderToken } from '@angular/core';

import { Registry } from './registry';
import { RegistryInitializer } from './registry.types';
import { RegistryPreset } from './registry-preset';

export function provideRegistry<T>(provideToken: InjectionToken<Registry<T>>, ...entryOrPreset: Array<RegistryInitializer<T>>): Provider {
  return {
    provide: provideToken,
    useFactory: () => {
      const parent = inject(provideToken, { skipSelf: true, optional: true });
      const service = new Registry<T>(parent);
      entryOrPreset.forEach((entryOrPresetFn) => entryOrPresetFn(service));
      return service;
    },
  };
}

const REGISTRY_INITIALIZER = Symbol('REGISTRY_INITIALIZER');

export function addPresetToRegistry<T>(
  presetKey: string,
  presetRegistryInjectionToken: ProviderToken<RegistryPreset<T>>
): RegistryInitializer<T> {
  const fn = (service: Registry<T>): void => {
    const presetRegistry = inject(presetRegistryInjectionToken);

    service.registerPreset({ presetKey, presetEntries: presetRegistry.entries() });
  };

  return defineProperty(fn);
}

export function addEntryToRegistry<T>(key: string, entry: NoInfer<T>, presetKey?: string): RegistryInitializer<T> {
  const fn = (service: Registry<T>): void => {
    service.registerEntry(key, entry, presetKey);
  };

  return defineProperty(fn);
}

export function isRegistryInitializer<T>(v: RegistryInitializer<T> | unknown): v is RegistryInitializer<T> {
  if (typeof v === 'function') {
    return Object.hasOwn(v, REGISTRY_INITIALIZER);
  }

  return false;
}

function defineProperty<T>(fn: T): T {
  return Object.defineProperty(fn, REGISTRY_INITIALIZER, { writable: false });
}
